
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { AlertCircle } from 'lucide-react';
import { useLocation } from '@/hooks/useLocation';
import { useVisitsData } from '@/hooks/useVisitsData';
import { useClockInOut } from '@/hooks/useClockInOut';
import LocationStatus from './LocationStatus';
import ActiveTimeRecord from './ActiveTimeRecord';
import VisitsList from './VisitsList';

const ClockInOut = () => {
  const { location, locationError, locationLoading, requestLocation } = useLocation();
  const { todaysVisits, activeTimeRecord, loading, fetchTodaysVisits, setActiveTimeRecord } = useVisitsData();
  const { loading: clockLoading, handleClockIn, handleClockOut } = useClockInOut(setActiveTimeRecord, fetchTodaysVisits);

  const onClockIn = async (visitId: string) => {
    try {
      const currentLocation = await requestLocation();
      await handleClockIn(visitId, currentLocation);
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  const onClockOut = async () => {
    if (!activeTimeRecord) return;
    try {
      const currentLocation = await requestLocation();
      await handleClockOut(activeTimeRecord, currentLocation);
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  if (loading) {
    return (
      <div className="p-4 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">Clock In/Out</h2>

      {/* Location Status */}
      <LocationStatus location={location} locationError={locationError} />

      {/* Active Time Record */}
      <ActiveTimeRecord 
        activeTimeRecord={activeTimeRecord}
        onClockOut={onClockOut}
        locationLoading={locationLoading || clockLoading}
        hasLocation={!!location}
      />

      {/* Today's Visits */}
      <VisitsList 
        visits={todaysVisits}
        onClockIn={onClockIn}
        locationLoading={locationLoading || clockLoading}
        hasLocation={!!location}
        hasActiveTimeRecord={!!activeTimeRecord}
      />

      {/* Instructions */}
      <Card className="mt-6 border-yellow-200 bg-yellow-50">
        <CardContent className="p-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
            <div className="text-sm text-yellow-800">
              <p className="font-medium mb-1">GPS Location Required</p>
              <p>This app requires location access for Electronic Visit Verification (EVV) compliance. Please enable location services when prompted.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClockInOut;
